import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "react-bootstrap/Card";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import RoomIcon from "@mui/icons-material/Room";
import ArrowCircleLeftRoundedIcon from "@mui/icons-material/ArrowCircleLeftRounded";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router";
import "styles/patient-form.scss";
import PatientFormService from "services/PatientFormService.js";
import FamilyService from "services/FamilyService.js";
import FormUtils from "utils/FormUtils.js";
import EnumUtils from "utils/EnumUtils.js";
import { CardActionArea } from "@mui/material";
import useAPI from "hooks/useAPI.js";

const PatientFormsList = () => {
  const navigate = useNavigate();
  const { intakeId, patientId } = useParams();
  const [formsList, setFormsList] = useState([]);

  const [patientDetails, setPatientDetails] = useState({
    patientId: "",
    patientName: "",
    hospitalName: "",
    ageGroup: "",
  });

  const getMemberBasicDetailsAPi = useAPI(
    FamilyService.getMemberBasicDetails,
    getMemberBasicDetailsHandler
  );
  const getFormsAPI = useAPI(
    PatientFormService.getIntakeForms,
    getFormsHandler
  );

  function getMemberBasicDetails() {
    getMemberBasicDetailsAPi.execute(patientId);
  }

  function getForms() {
    getFormsAPI.execute(patientId, intakeId);
  }

  useEffect(() => {
    getMemberBasicDetails();
    getForms();
  }, []);


  /* API Success Handlers */
  function getMemberBasicDetailsHandler(response) {
    let data = response.data;
    setPatientDetails(data);
  }

  function getFormsHandler(response) {
    setFormsList(response.data || []);
  }

  // Define consent form types
  const CONSENT_FORM_TYPES = [
    "PATIENT_CONSENT",
    "NEURODEVELOPMENTAL_SERVICES_CONSENT",
    "LENS_NFB_CONSENT",
    "QEEG_EQUIP_CONSENT"
  ];

  // Logic to separate active and historical forms
  const getProcessedForms = () => {
    let activeForms = [];
    let historicalForms = [];

    // Group forms by type
    const formsByType = {};
    formsList.forEach(form => {
      if (!formsByType[form.registrationFormTypeEnum]) {
        formsByType[form.registrationFormTypeEnum] = [];
      }
      formsByType[form.registrationFormTypeEnum].push(form);
    });

    // Process groups
    Object.keys(formsByType).forEach(type => {
      const forms = formsByType[type];

      // If it's a consent form and there are multiple, sort and split
      if (CONSENT_FORM_TYPES.includes(type) && forms.length > 0) {
        // Sort by ID descending (assuming higher ID is newer)
        // If createdAt is available, it would be better, but ID is a safe proxy for insertion order
        const sortedForms = forms.sort((a, b) => b.id - a.id);

        // Latest is active
        activeForms.push(sortedForms[0]);

        // Others are historical
        if (sortedForms.length > 1) {
          historicalForms.push(...sortedForms.slice(1));
        }
      } else {
        // Not a consent form, or single consent form -> all active
        activeForms.push(...forms);
      }
    });

    // Sort active forms by ID or Name to maintain some order if needed
    activeForms.sort((a, b) => a.id - b.id);

    return { activeForms, historicalForms };
  };

  const { activeForms, historicalForms } = getProcessedForms();

  const renderFormCard = (form, index) => (
    <Card
      key={form.id || index}
      className={`card card-table-width auto-height ${CONSENT_FORM_TYPES.includes(form.registrationFormTypeEnum) && historicalForms.includes(form) ? 'historical-form' : ''}`}
      onClick={() =>
        navigateToForm(
          form.registrationFormTypeEnum,
          form.formStatusTypeEnum,
          patientId,
          intakeId,
          form.id
        )
      }
      style={CONSENT_FORM_TYPES.includes(form.registrationFormTypeEnum) && historicalForms.includes(form) ? { opacity: 0.7, backgroundColor: '#f5f5f5' } : {}}
    >
      <CardActionArea>
        <Card.Body>
          <div className="blue-font-color display-inline-block">
            <ArrowRightIcon />
            <span>
              {EnumUtils.parse(form.registrationFormTypeEnum)}
            </span>
          </div>
          <div className="display-inline">
            <span className={`right-align height-2-rem large-fonts`}>
              {FormUtils.getStatusChip(form.formStatusTypeEnum)}
            </span>
          </div>
          <div className="margin-above">
            <span>Saved on: </span>
            <span className="green-font">{form.lastUpdatedAt}</span>
          </div>
        </Card.Body>
      </CardActionArea>
    </Card>
  );

  return (<div className="registration-forms-container">
    <h2>Forms to fill in before consultation</h2>
    <p>
      Please fill in the form below. That way the consultant will be prepared
      for your session.
    </p>
    <Grid container spacing={4}>
      <Grid item xs={9}>
        {getFormsAPI.status === "pending" ? (
          <div className="text-center">
            <CircularProgress
              className="text-center"
              sx={{
                mt: 50,
              }}
            />
          </div>
        ) : (
          <>
            {activeForms.map((form, index) => renderFormCard(form, index))}

            {historicalForms.length > 0 && (
              <div style={{ marginTop: '40px', borderTop: '1px solid #ddd', paddingTop: '20px', width: '100%' }}>
                <h4 style={{ color: '#666', marginBottom: '20px' }}>Historical / Previous Versions</h4>
                {historicalForms.map((form, index) => renderFormCard(form, index))}
              </div>
            )}
          </>
        )}
      </Grid>
      <Grid item xs={3}>
        <Card className="card card-table-width border-color2 auto-height">
          <Card.Body>
            <div>
              <p className="purple-font-color">
                Patient Id: {patientDetails.patientId}
              </p>
              <p className="purple-font-color margin-left-room">
                <RoomIcon /> {patientDetails.hospitalName}
              </p>
              <p className="large-font">{patientDetails.patientName}</p>
              <p className="medium-font">{patientDetails.ageGroup}</p>
            </div>
          </Card.Body>
        </Card>
        <Card
          className="card card-table-width auto-height hover-effect"
          onClick={() => window.open("https://melillomethod.powerofpatients.com/signIn", "_blank")}
        >
          <CardActionArea>
            <Card.Body className="text-center">
              <div className="sallie-signup-container">
                <h5 className="gradient-text">Sign up on <i>Sallie</i></h5>
                <p className="small-text">Get access to more resources and track your progress</p>
                <div className="cta-button">
                  Join Now <ArrowRightIcon fontSize="small" />
                </div>
              </div>
            </Card.Body>
          </CardActionArea>
        </Card>

      </Grid>
      <Grid item xs={9}>
        <button className="back" onClick={() => navigate(-1)}>
          <ArrowCircleLeftRoundedIcon fontSize="medium" />
          Back
        </button>
      </Grid>
    </Grid>
  </div>
  );
}


function navigateToForm(
  patientFormTypeEnum,
  formStatus,
  patientId,
  intakeId,
  formId
) {
  let viewMode = "write";
  if (formStatus === "SUBMITTED") viewMode = "read";

  if (patientFormTypeEnum === "DEMOGRAPHIC_INFO_CHECKLIST_CHILD") {
    window.open(
      `user-forms/patient-registration/child-demographic-info-checklist.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "HISTORY_CHILD") {
    window.open(
      `user-forms/patient-registration/child-history.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "HEMISPHERIC_CHECKLIST_CHILD") {
    window.open(
      `user-forms/patient-registration/child-hemispheric-checklist.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "DEMOGRAPHIC_INFO_CHECKLIST_ADULT") {
    window.open(
      `user-forms/patient-registration/adult-demographic-info-checklist.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "HISTORY_ADULT") {
    window.open(
      `user-forms/patient-registration/adult-history.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "HEMISPHERIC_CHECKLIST_ADULT") {
    window.open(
      `user-forms/patient-registration/adult-hemispheric-checklist.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "COGNITIVE_STYLE_ASSESSMENT_ADULT") {
    window.open(
      `user-forms/patient-registration/adult-cognitive-style-assessment.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "SENSORY_FUNCTION_CHECKLIST_ADULT") {
    window.open(
      `user-forms/patient-registration/adult-sensory-function-checklist.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "PATIENT_CONSENT") {
    window.open(
      `user-forms/patient-registration/patient-consent.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "HISTORY_CHILD_V2") {
    window.open(
      `user-forms/patient-registration/child-history-v2.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "HISTORY_ADULT_V2") {
    window.open(
      `user-forms/patient-registration/adult-history-v2.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "ADULT_FOLLOW_UP") {
    window.open(
      `user-forms/patient-registration/adult-follow-up.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "CHILD_FOLLOW_UP") {
    window.open(
      `user-forms/patient-registration/child-follow-up.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "LENS_NFB_CONSENT") {
    window.open(
      `user-forms/patient-registration/lens-consent.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "NEURODEVELOPMENTAL_SERVICES_CONSENT") {
    window.open(
      `user-forms/patient-registration/neurodevelopmental-consent.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "MOTHER_HEMISPHERIC_CHECKLIST") {
    window.open(
      `user-forms/patient-registration/mother-hemispheric-checklist.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "FATHER_HEMISPHERIC_CHECKLIST") {
    window.open(
      `user-forms/patient-registration/father-hemispheric-checklist.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else {
    alert("Invalid Patient Assessment Form Type");
  }
}

export default PatientFormsList;